import CIMSymbol from '@arcgis/core/symbols/CIMSymbol'
import {
  UniqueValueRenderer,
  SimpleRenderer,
  ClassBreaksRenderer
} from '@arcgis/core/renderers'
import UniqueValueInfo from '@arcgis/core/renderers/support/UniqueValueInfo'
import ClassBreakInfo from '@arcgis/core/renderers/support/ClassBreakInfo'

import {
  IColorByValueAttributeMap,
  IColorByValueAttributeSegmentColorProfile
} from 'app/services/api/colorByValue'
import { formatHexToEsriRgb } from 'common/utils/colorUtils'

import tailwind from '_/tailwind.config'

// Reversal corrects the map legend
const REVERSE_CBV_MAP = true

const createRendererLinearSymbol = (color: string) => {
  return new CIMSymbol({
    data: {
      type: 'CIMSymbolReference',
      symbol: {
        type: 'CIMLineSymbol',
        symbolLayers: [
          {
            type: 'CIMSolidStroke',
            enable: true,
            capStyle: 'Butt',
            joinStyle: 'Round',
            width: 4,
            color: formatHexToEsriRgb(color)
          }
        ]
      }
    }
  })
}

const createRendererPointSymbol = (color: string) => {
  return new CIMSymbol({
    data: {
      type: 'CIMSymbolReference',
      symbol: {
        type: 'CIMPointSymbol',
        symbolLayers: [
          {
            type: 'CIMVectorMarker',
            enable: true,
            anchorPoint: {
              x: 0,
              y: 0
            },
            anchorPointUnits: 'Relative',
            dominantSizeAxis3D: 'Y',
            size: 6,
            billboardMode3D: 'FaceNearPlane',
            frame: {
              xmin: 0,
              ymin: 0,
              xmax: 17,
              ymax: 17
            },
            markerGraphics: [
              {
                type: 'CIMMarkerGraphic',
                geometry: {
                  rings: [
                    [
                      [8.5, 0],
                      [7.02, 0.13],
                      [5.59, 0.51],
                      [4.25, 1.14],
                      [3.04, 1.99],
                      [1.99, 3.04],
                      [1.14, 4.25],
                      [0.51, 5.59],
                      [0.13, 7.02],
                      [0, 8.5],
                      [0.13, 9.98],
                      [0.51, 11.41],
                      [1.14, 12.75],
                      [1.99, 13.96],
                      [3.04, 15.01],
                      [4.25, 15.86],
                      [5.59, 16.49],
                      [7.02, 16.87],
                      [8.5, 17],
                      [9.98, 16.87],
                      [11.41, 16.49],
                      [12.75, 15.86],
                      [13.96, 15.01],
                      [15.01, 13.96],
                      [15.86, 12.75],
                      [16.49, 11.41],
                      [16.87, 9.98],
                      [17, 8.5],
                      [16.87, 7.02],
                      [16.49, 5.59],
                      [15.86, 4.25],
                      [15.01, 3.04],
                      [13.96, 1.99],
                      [12.75, 1.14],
                      [11.41, 0.51],
                      [9.98, 0.13],
                      [8.5, 0]
                    ]
                  ]
                },
                symbol: {
                  type: 'CIMPolygonSymbol',
                  symbolLayers: [
                    {
                      type: 'CIMSolidStroke',
                      enable: true,
                      capStyle: 'Round',
                      joinStyle: 'Round',
                      lineStyle3D: 'Strip',
                      miterLimit: 10,
                      width: 0,
                      color: [0, 0, 0, 255]
                    },
                    {
                      type: 'CIMSolidFill',
                      enable: true,
                      color: formatHexToEsriRgb(color)
                    }
                  ]
                }
              }
            ],
            scaleSymbolsProportionally: true,
            respectFrame: true
          }
        ]
      }
    }
  })
}

const getSymbolRenderer: Record<string, (color: string) => CIMSymbol> = {
  Linear: createRendererLinearSymbol,
  Point: createRendererPointSymbol
}

export const createDefaultRenderer = (
  typeCode: string | undefined
): SimpleRenderer => {
  if (typeCode) {
    const symbolRenderer = getSymbolRenderer[typeCode]
    const symbol: CIMSymbol = symbolRenderer(tailwind.theme.colors.gray[700])

    return new SimpleRenderer({
      symbol
    })
  }

  return new SimpleRenderer()
}

const getExtraProps = {
  number: (
    item: IColorByValueAttributeSegmentColorProfile,
    isFirst = false,
    isLast = false
  ) => {
    /**
     * NOTE: We are going against ArcGIS norm where maxValue is
     *       always exclusive while min value except first value
     *       is non inclusive. (See LHT-2027)
     *
     * P.S. We are using a fixed small number here as something
     *      like Number.EPSILON will get rounded to integer when
     *      subtracted.. (hope we don't get very small values)
     */
    const NUMBER_DELTA = 1e-8

    const res = {
      label: item.value.number?.min + ' - ' + item.value.number?.max,
      minValue: 0,
      maxValue: 0
    }

    const minValue = item.value.number?.min
    const maxValue = item.value.number?.max

    if (minValue != null) {
      if ((!REVERSE_CBV_MAP && isFirst) || (REVERSE_CBV_MAP && isLast)) {
        res.minValue = minValue
      } else {
        res.minValue = minValue - NUMBER_DELTA
      }
    }

    if (maxValue != null) {
      if ((!REVERSE_CBV_MAP && isLast) || (REVERSE_CBV_MAP && isFirst)) {
        res.maxValue = maxValue
      } else {
        res.maxValue = maxValue - NUMBER_DELTA
      }
    }

    return res
  },
  boolean: (item: IColorByValueAttributeSegmentColorProfile) => {
    return {
      label: String(item.value.boolean),
      value: Number(item.value.boolean)
    }
  },
  string: (item: IColorByValueAttributeSegmentColorProfile) => {
    return {
      label: item.value.string?.name,
      value: item.value.string?.code
    }
  },
  date: (item: IColorByValueAttributeSegmentColorProfile) => {
    const minValue = item.value.date?.minDate
      ? new Date(item.value.date?.minDate).getTime()
      : 0
    const maxValue = item.value.date?.maxDate
      ? new Date(item.value.date?.maxDate).getTime()
      : 0

    const res = {
      label: item.value.date?.minDate + ' - ' + item.value.date?.maxDate,
      minValue: minValue,
      maxValue: maxValue
    }

    return res
  }
}

// update the maxDate for last range of selected displayAttribute to add on day
export const updateAttribute = (
  displayAttribute: IColorByValueAttributeMap
): IColorByValueAttributeMap => {
  return {
    ...displayAttribute,
    configurations: displayAttribute.configurations.map((config, index) => {
      if (index === 0 && config.value.date !== undefined) {
        const currentMaxDate = new Date(config.value.date.maxDate)

        currentMaxDate.setDate(currentMaxDate.getDate() + 1) // Add one day

        return {
          ...config,
          value: {
            ...config.value,
            date: {
              ...config.value.date,
              maxDate: currentMaxDate.toISOString().slice(0, 10) // Update maxDate with the new date
            }
          }
        }
      }

      return config // Keep the rest of the configurations unchanged
    })
  }
}

const createClassBreakInfoRenderer = (
  displayAttribute: IColorByValueAttributeMap,
  displayAttributeColumnName: string | undefined,
  typeCode: string
) => {
  const extraProps = getExtraProps[displayAttribute.attributeType]
  const symbolRenderer = getSymbolRenderer[typeCode]

  return new ClassBreaksRenderer({
    legendOptions: {
      title: displayAttribute.attributeName ?? ''
    },
    field: displayAttributeColumnName,
    classBreakInfos: displayAttribute.configurations.map((item, i, items) => {
      return new ClassBreakInfo({
        ...extraProps(item, i == 0, i == items.length - 1),
        symbol: symbolRenderer(item.color)
      })
    }),
    defaultSymbol: symbolRenderer(tailwind.theme.colors.gray[400])
  })
}

const createUniqueValueRenderer = (
  displayAttribute: IColorByValueAttributeMap,
  displayAttributeColumnName: string | undefined,
  typeCode: string
) => {
  const extraProps = getExtraProps[displayAttribute.attributeType]
  const symbolRenderer = getSymbolRenderer[typeCode]

  return new UniqueValueRenderer({
    legendOptions: {
      title: displayAttribute.attributeName ?? ''
    },
    field: displayAttributeColumnName,
    uniqueValueInfos: displayAttribute.configurations.map((item, i, items) => {
      return new UniqueValueInfo({
        ...extraProps(item, i == 0, i == items.length - 1),
        symbol: symbolRenderer(item.color)
      })
    }),
    defaultSymbol: symbolRenderer(tailwind.theme.colors.gray[400])
  })
}

export const createCustomRenderer = (
  displayAttribute: IColorByValueAttributeMap,
  displayAttributeColumnName: string | undefined,
  typeCode: string | undefined
) => {
  if (typeCode) {
    /**
     * Configuration list reversal make the legend display in correct
     * order for unknown reasons..
     */
    const displayAttributeWithReversedColorMap = {
      ...displayAttribute,
      configurations: REVERSE_CBV_MAP
        ? [...displayAttribute.configurations].reverse()
        : [...displayAttribute.configurations]
    }

    switch (displayAttribute.attributeType) {
      case 'string':
      case 'boolean':
        return createUniqueValueRenderer(
          displayAttributeWithReversedColorMap,
          displayAttributeColumnName,
          typeCode
        )
      case 'number':
        return createClassBreakInfoRenderer(
          displayAttributeWithReversedColorMap,
          displayAttributeColumnName,
          typeCode
        )
      case 'date': {
        const updatedDisplayAttribute = updateAttribute(
          displayAttributeWithReversedColorMap
        )

        return createClassBreakInfoRenderer(
          updatedDisplayAttribute,
          displayAttributeColumnName,
          typeCode
        )
      }

      default:
        return createDefaultRenderer(typeCode)
    }
  }
}
